import { Auth } from 'aws-amplify'
import { useFormik } from 'formik'
import React from 'react'
import { DataLayerArgs, TagManagerArgs } from 'react-gtm-module'
import { captureException } from 'service-api/src/shared/monitoring'
import * as Yup from 'yup'

import { generatePassword } from '@shared/common'
import { i18WithDefault as t } from '@shared/locales'
import { EnterEmailForm } from './enter_email_form'
import { AuthException } from './errors'
import { VerifyEmailForm } from './verify_email_form'

export type TFormikValues = {
  email: string
  inputCode: boolean
  code: string
}

export type ChallengeResponseType = {
  username: string
  answer: string
}

type SignInTooltipContentProps = {
  onError?: (error: Error) => void
  onSuccessfulSignIn?: () => void
  onFailedSignIn?: () => void
  simpleView?: boolean
  onChange?: (value: string) => void
  continueLabel?: string
  showSignUpForm?: boolean
  onCodeRequested?: () => void
  onUseDifferentEmail?: () => void
  registerIfNeeded?: boolean
  onRegistering?: () => void
  onSuccessfulRegistration?: () => void
  disableSubmit?: boolean
  newUserEmail?: string
  TagManager?: {
    dataLayer: (dataLayerArgs: DataLayerArgs) => void
    initialize: (tagManagerArgs: TagManagerArgs) => void
  }
}

export const SignInTooltipContent = ({
  onError,
  onSuccessfulSignIn,
  onFailedSignIn,
  simpleView = false,
  onChange,
  onCodeRequested,
  onUseDifferentEmail,
  continueLabel,
  showSignUpForm = false,
  registerIfNeeded = false,
  onRegistering,
  onSuccessfulRegistration,
  disableSubmit,
  newUserEmail,
  TagManager,
}: SignInTooltipContentProps) => {
  const [showVerifyForm, setShowVerifyForm] = React.useState(false)
  const [cognitoUser, setCognitoUser] = React.useState(null)
  const [isNewUser, setIsNewUser] = React.useState(false)

  const formik = useFormik<TFormikValues>({
    initialValues: {
      email: newUserEmail || '',
      inputCode: false,
      code: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t('global.label.invalidEmail')).required(t('global.label.required')),
      inputCode: Yup.boolean(),
      code: Yup.string().when('inputCode', {
        is: true,
        then: Yup.string().length(6, t('global.label.invalidCode')).required(t('global.label.required')),
      }),
    }),
    onSubmit: async ({ email, code }, { setSubmitting }) => {
      if (email) {
        const userEmail = email.toLowerCase().trim()

        if (!code && !showSignUpForm) {
          TagManager?.dataLayer({ dataLayer: { event: 'LOGIN_EMAIL' } })

          try {
            const signInResult = await Auth.signIn(userEmail)
            setCognitoUser(signInResult)
            setShowVerifyForm(true)
            onCodeRequested?.()
            formik.setFieldValue('inputCode', true)
            formik.setTouched({ code: false })
          } catch {
            if (!registerIfNeeded) {
              onFailedSignIn?.()
              return
            }

            // if the signin fails and requires registration, the fail signin
            // means that the user is not registered yet, try to sign up

            onRegistering?.()
            try {
              await Auth.signUp({
                username: userEmail,
                password: generatePassword(),
                attributes: {
                  email: userEmail,
                },
                autoSignIn: {
                  enabled: true,
                },
              })
              setIsNewUser(true)
              setShowVerifyForm(true)
              onCodeRequested?.()
              onSuccessfulRegistration?.()
              formik.setFieldValue('inputCode', true)
              formik.setTouched({ code: false })
            } catch (_error) {
              captureException(new AuthException('Auth flow failed'), {
                fingerprint: ['auth-exception'],
                tags: [{ key: 'auth.flow', value: 'email_registration' }],
                user: { id: 'unknown', email: userEmail },
                context: { cause: (_error as Error).message },
              })
              onFailedSignIn?.()
            }
          } finally {
            setSubmitting(false)
          }
        } else if (cognitoUser) {
          // after the user sign in, the code is required to complete the sign
          // in process
          TagManager?.dataLayer({ dataLayer: { event: 'LOGIN_CODE' } })
          const sanitizedCode = code.replace(/\s/g, '')
          try {
            const challengeResult = await Auth.sendCustomChallengeAnswer(cognitoUser, sanitizedCode)
            if (challengeResult.signInUserSession) {
              onSuccessfulSignIn?.()
              TagManager?.dataLayer({ dataLayer: { event: 'login' } })
            } else {
              formik.setFieldError('code', t('global.label.wrongCode'))
              captureException(new AuthException('Auth flow failed'), {
                fingerprint: ['auth-exception'],
                tags: [{ key: 'auth.flow', value: 'login_code' }],
                user: { id: 'unknown', email: userEmail },
                context: { code: sanitizedCode },
              })
            }
          } catch (error) {
            captureException(new AuthException('Auth flow failed'), {
              fingerprint: ['auth-exception'],
              tags: [{ key: 'auth.flow', value: 'login_code' }],
              user: { id: 'unknown', email: userEmail },
              context: { cause: (error as Error).message, code: sanitizedCode },
            })
            formik.setFieldError('code', t('global.verifyCodeError'))
            onError?.(error as Error)
          } finally {
            setSubmitting(false)
          }
        } else if (showSignUpForm && !isNewUser) {
          try {
            await Auth.signUp({
              username: userEmail,
              password: generatePassword(),
              attributes: {
                email: userEmail,
              },
              autoSignIn: {
                enabled: true,
              },
            })
            setIsNewUser(true)
            setShowVerifyForm(true)
            onCodeRequested?.()
            formik.setFieldValue('inputCode', true)
            formik.setTouched({ code: false })
          } catch {
            onFailedSignIn?.()
          } finally {
            setSubmitting(false)
          }
        } else if (isNewUser) {
          TagManager?.dataLayer({ dataLayer: { event: 'REGISTER_CODE' } })

          try {
            await Auth.confirmSignUp(userEmail, code)
            onSuccessfulSignIn?.()
            onCodeRequested?.()
            TagManager?.dataLayer({ dataLayer: { event: 'sign_up' } })
          } catch (error) {
            captureException(new AuthException('Auth flow failed'), {
              fingerprint: ['auth-exception'],
              tags: [{ key: 'auth.flow', value: 'registration_code' }],
              user: { id: 'unknown', email: userEmail },
              context: { cause: (error as Error).message, code },
            })
            formik.setFieldError('code', t('global.verifyCodeError'))
            onError?.(error as Error)
          } finally {
            setSubmitting(false)
          }
        }
      }
    },
  })

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        {showVerifyForm ? (
          <VerifyEmailForm
            formik={formik}
            setShowVerifyForm={setShowVerifyForm}
            setCognitoUser={setCognitoUser}
            simpleView={simpleView}
            isNewUser={isNewUser}
            onUseDifferentEmail={onUseDifferentEmail}
          />
        ) : (
          <EnterEmailForm
            formik={formik}
            simpleView={simpleView}
            onChange={onChange}
            continueLabel={continueLabel}
            disableSubmit={disableSubmit}
          />
        )}
      </form>
    </>
  )
}

export default SignInTooltipContent
